import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Chip, Grid, Stack, Skeleton,
} from '@mui/material';
import {
  Target, TrendingUp, Map, Award, Users, Brain,
  Settings, BarChart3, Globe, Briefcase, Activity,
  Star, CheckCircle, ArrowRight, Calendar, MessageCircle,
  Play, DollarSign, Building, Rocket, Lightbulb,
  Trophy, Network, Compass, Route, Flag, Zap,
  BookOpen, GraduationCap, Leaf, LineChart,
  User, ChevronRight, Heart, Sparkles, MapPin,
  ExternalLink, TrendingDown, Cpu, Code
} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import SkillDemandChart from '../../components/Career/SkillDemandChart';
import EmergingSkills from '../../components/Career/EmergingSkills';
import RecentJobs from '../../components/Career/RecentJobs';
import careerService from '../../services/careerService';
import { CareerApi } from '../../services/api';

/* ─── Design tokens ─────────────────────────────────────────────────── */
const BG        = '#09090b';
const CARD_BG   = 'rgba(255,255,255,0.025)';
const CARD_BDR  = '1px solid rgba(255,255,255,0.08)';
const CARD_R    = '12px';
const H_COLOR   = '#fafafa';
const SEC_COLOR = '#a1a1aa';
const MUT_COLOR = '#71717a';
const AMBER     = '#f59e0b';
const AMBER_DIM = 'rgba(245,158,11,0.15)';
const HOVER_BDR = 'rgba(255,255,255,0.15)';

const cardSx = {
  background: CARD_BG,
  border: CARD_BDR,
  borderRadius: CARD_R,
  transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: HOVER_BDR,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  },
};

const skillChipSx = {
  background: 'rgba(255,255,255,0.08)',
  color: SEC_COLOR,
  fontSize: '0.75rem',
  height: 26,
  borderRadius: '6px',
};

/* ─── Mock career-path data (5 paths) ───────────────────────────────── */
const CAREER_PATHS = {
  'process-engineer': {
    title: 'Process Engineer',
    description: 'Lead process optimisation and manufacturing excellence',
    icon: React.createElement(Settings, { size: 22 }),
    accent: '#f59e0b',
    avgSalary: '$85K \u2013 $150K',
    demandLevel: 'High',
    demandColor: '#f59e0b',
    growthRate: '12% annually',
    skills: ['Process Design', 'Lean Manufacturing', 'Six Sigma', 'Project Mgmt'],
    industries: ['Manufacturing', 'Chemical', 'Food & Beverage', 'Pharma'],
    roadmap: {
      junior: {
        title: 'Junior Process Engineer', duration: '0\u20132 yrs', salary: '$65K \u2013 $85K',
        skills: ['Basic process principles', 'CAD software', 'Data collection', 'Safety protocols'],
        responsibilities: ['Assist in process design', 'Data analysis', 'Documentation', 'Process monitoring'],
        courses: ['Process Fundamentals', 'Engineering Math', 'CAD Basics', 'Safety Training'],
        nextSteps: ['Gain hands-on experience', 'Complete safety certifications', 'Learn process software'],
      },
      mid: {
        title: 'Process Engineer', duration: '2\u20135 yrs', salary: '$85K \u2013 $120K',
        skills: ['Process optimisation', 'Lean principles', 'Statistical analysis', 'Team leadership'],
        responsibilities: ['Lead improvements', 'Project management', 'Cross-func collaboration', 'Cost optimisation'],
        courses: ['Lean Manufacturing', 'Six Sigma Green Belt', 'Advanced Statistics', 'Leadership Skills'],
        nextSteps: ['Lead major projects', 'Obtain Six Sigma cert', 'Develop team mgmt skills'],
      },
      senior: {
        title: 'Senior Process Engineer / Manager', duration: '5+ yrs', salary: '$120K \u2013 $180K+',
        skills: ['Strategic planning', 'Advanced optimisation', 'People management', 'Business acumen'],
        responsibilities: ['Strategic process planning', 'Team management', 'Budget oversight', 'Innovation leadership'],
        courses: ['Executive Leadership', 'Strategic Planning', 'Advanced Process Control', 'Business Strategy'],
        nextSteps: ['Consider C-level positions', 'Industry thought leadership', 'Consulting opportunities'],
      },
    },
  },
  'ai-engineer': {
    title: 'AI Engineer',
    description: 'Develop and deploy AI solutions for industrial applications',
    icon: React.createElement(Brain, { size: 22 }),
    accent: '#8b5cf6',
    avgSalary: '$95K \u2013 $180K',
    demandLevel: 'Very High',
    demandColor: '#ef4444',
    growthRate: '22% annually',
    skills: ['Machine Learning', 'Python', 'Deep Learning', 'MLOps'],
    industries: ['Technology', 'Manufacturing', 'Healthcare', 'Finance'],
    roadmap: {
      junior: {
        title: 'Junior AI Engineer', duration: '0\u20132 yrs', salary: '$75K \u2013 $95K',
        skills: ['Python programming', 'ML basics', 'Statistics', 'Data preprocessing'],
        responsibilities: ['Model training', 'Data analysis', 'Code implementation', 'Testing'],
        courses: ['Python Fundamentals', 'Machine Learning Basics', 'Statistics', 'Data Science'],
        nextSteps: ['Build portfolio projects', 'Learn advanced algorithms', 'Practice with real datasets'],
      },
      mid: {
        title: 'AI Engineer', duration: '2\u20135 yrs', salary: '$95K \u2013 $140K',
        skills: ['Advanced ML', 'Deep learning', 'Model deployment', 'System design'],
        responsibilities: ['End-to-end ML projects', 'Model optimisation', 'Production deployment', 'Mentoring'],
        courses: ['Deep Learning', 'MLOps', 'Cloud Platforms', 'Advanced Algorithms'],
        nextSteps: ['Specialise in domain', 'Lead AI initiatives', 'Contribute to open source'],
      },
      senior: {
        title: 'Senior AI Engineer / AI Architect', duration: '5+ yrs', salary: '$140K \u2013 $200K+',
        skills: ['AI strategy', 'System architecture', 'Team leadership', 'Research'],
        responsibilities: ['AI strategy', 'Architecture design', 'Team leadership', 'Innovation'],
        courses: ['AI Strategy', 'Research Methods', 'Executive Leadership', 'Emerging Technologies'],
        nextSteps: ['Chief AI Officer', 'AI consultant', 'Research leadership'],
      },
    },
  },
  'sustainability-engineer': {
    title: 'Sustainability Engineer',
    description: 'Drive environmental initiatives and sustainable engineering practices',
    icon: React.createElement(Leaf, { size: 22 }),
    accent: '#10b981',
    avgSalary: '$70K \u2013 $130K',
    demandLevel: 'Growing',
    demandColor: '#10b981',
    growthRate: '15% annually',
    skills: ['ESG Frameworks', 'Environmental Assessment', 'Green Design', 'Renewable Energy'],
    industries: ['Energy', 'Manufacturing', 'Construction', 'Consulting'],
    roadmap: {
      junior: {
        title: 'Sustainability Analyst', duration: '0\u20132 yrs', salary: '$55K \u2013 $75K',
        skills: ['Environmental science', 'Data analysis', 'Sustainability metrics', 'Reporting'],
        responsibilities: ['Environmental assessments', 'Data collection', 'Report generation', 'Compliance monitoring'],
        courses: ['Environmental Science', 'Sustainability Fundamentals', 'ESG Reporting', 'Green Technologies'],
        nextSteps: ['Gain certification', 'Learn ESG frameworks', 'Build assessment skills'],
      },
      mid: {
        title: 'Sustainability Engineer', duration: '2\u20135 yrs', salary: '$75K \u2013 $110K',
        skills: ['Life cycle assessment', 'Green design', 'Project management', 'Stakeholder engagement'],
        responsibilities: ['Sustainability projects', 'Green initiatives', 'Stakeholder coordination', 'Impact measurement'],
        courses: ['Life Cycle Assessment', 'Green Engineering', 'Project Management', 'Stakeholder Management'],
        nextSteps: ['Lead major initiatives', 'Obtain advanced certifications', 'Build industry network'],
      },
      senior: {
        title: 'Chief Sustainability Officer', duration: '5+ yrs', salary: '$110K \u2013 $160K+',
        skills: ['Strategic planning', 'Corporate sustainability', 'Leadership', 'Policy development'],
        responsibilities: ['Sustainability strategy', 'Executive leadership', 'Policy development', 'Industry advocacy'],
        courses: ['Executive Leadership', 'Strategic Sustainability', 'Policy Development', 'Corporate Governance'],
        nextSteps: ['C-suite positions', 'Board roles', 'Sustainability consulting'],
      },
    },
  },
  'data-engineer': {
    title: 'Data Engineer',
    description: 'Build scalable data pipelines and infrastructure for analytics',
    icon: React.createElement(LineChart, { size: 22 }),
    accent: '#06b6d4',
    avgSalary: '$90K \u2013 $160K',
    demandLevel: 'High',
    demandColor: '#f59e0b',
    growthRate: '18% annually',
    skills: ['SQL / NoSQL', 'Apache Spark', 'Cloud Platforms', 'ETL Pipelines'],
    industries: ['Technology', 'Finance', 'Healthcare', 'Retail'],
    roadmap: {
      junior: {
        title: 'Junior Data Engineer', duration: '0\u20132 yrs', salary: '$70K \u2013 $90K',
        skills: ['SQL fundamentals', 'Python scripting', 'Data modelling', 'Cloud basics'],
        responsibilities: ['Maintain pipelines', 'Data cleaning', 'Documentation', 'Query optimisation'],
        courses: ['SQL Mastery', 'Python for Data', 'Cloud Fundamentals', 'Database Design'],
        nextSteps: ['Learn streaming technologies', 'Get cloud certified', 'Build end-to-end projects'],
      },
      mid: {
        title: 'Data Engineer', duration: '2\u20135 yrs', salary: '$90K \u2013 $130K',
        skills: ['Spark / Flink', 'Data lake design', 'Real-time streaming', 'DataOps'],
        responsibilities: ['Design data architecture', 'Optimise performance', 'Lead migrations', 'Mentoring'],
        courses: ['Apache Spark', 'Kafka & Streaming', 'Airflow Orchestration', 'Data Governance'],
        nextSteps: ['Architect multi-cloud solutions', 'Lead data platform team', 'Publish technical blog'],
      },
      senior: {
        title: 'Lead / Principal Data Engineer', duration: '5+ yrs', salary: '$130K \u2013 $185K+',
        skills: ['Platform architecture', 'Multi-cloud strategy', 'Team leadership', 'Cost optimisation'],
        responsibilities: ['Data platform strategy', 'Cross-org standards', 'Vendor evaluation', 'Hiring'],
        courses: ['Data Platform Design', 'Engineering Management', 'FinOps', 'ML Platform Engineering'],
        nextSteps: ['Head of Data Engineering', 'Chief Data Officer', 'Principal Consultant'],
      },
    },
  },
  'industrial-iot-engineer': {
    title: 'Industrial IoT Engineer',
    description: 'Connect industrial systems through sensors, automation and edge computing',
    icon: React.createElement(Cpu, { size: 22 }),
    accent: '#f97316',
    avgSalary: '$80K \u2013 $145K',
    demandLevel: 'High',
    demandColor: '#f59e0b',
    growthRate: '20% annually',
    skills: ['PLC / SCADA', 'Edge Computing', 'MQTT / OPC-UA', 'Cybersecurity'],
    industries: ['Manufacturing', 'Energy', 'Smart Cities', 'Logistics'],
    roadmap: {
      junior: {
        title: 'IoT Systems Technician', duration: '0\u20132 yrs', salary: '$60K \u2013 $80K',
        skills: ['Sensor integration', 'Networking basics', 'PLC programming', 'Data logging'],
        responsibilities: ['Device commissioning', 'Troubleshooting', 'Documentation', 'Monitor dashboards'],
        courses: ['Industrial Networking', 'PLC Fundamentals', 'IoT Protocols', 'Cybersecurity Basics'],
        nextSteps: ['Earn CISA or ICS cert', 'Learn edge platforms', 'Build test-bed projects'],
      },
      mid: {
        title: 'Industrial IoT Engineer', duration: '2\u20135 yrs', salary: '$80K \u2013 $120K',
        skills: ['Edge AI deployment', 'OT/IT convergence', 'Digital twin modelling', 'Protocol bridging'],
        responsibilities: ['Design IoT architecture', 'Integrate OT systems', 'Security reviews', 'Mentoring'],
        courses: ['Digital Twin Engineering', 'Edge AI', 'Industrial Cybersecurity', 'Cloud IoT Platforms'],
        nextSteps: ['Lead smart-factory pilots', 'Publish case studies', 'Develop IP'],
      },
      senior: {
        title: 'IoT Solutions Architect', duration: '5+ yrs', salary: '$120K \u2013 $170K+',
        skills: ['Enterprise architecture', 'Strategic planning', 'Standards bodies', 'P&L ownership'],
        responsibilities: ['Platform strategy', 'Enterprise rollout', 'Partner ecosystems', 'C-suite reporting'],
        courses: ['Enterprise Architecture', 'IIoT Strategy', 'Innovation Management', 'Business Cases'],
        nextSteps: ['CTO / VP Engineering', 'Entrepreneur / startup', 'Industry standards board'],
      },
    },
  },
};

const MENTORS = [
  {
    name: 'Dr. Moise Ugwiri',
    title: 'Founder & Lead Researcher',
    expertise: 'Process Engineering, Industrial AI',
    experience: 'Q1 2026',
    students: 'Coming Soon',
    rating: 'Future',
    avatar: '\ud83d\udc68\u200d\ud83d\udd2c',
    specialties: ['PhD Industrial Engineering', 'STMicroelectronics Engineer', '27+ Publications'],
  },
  {
    name: 'Future AI Expert',
    title: 'Senior AI Research Director',
    expertise: 'Machine Learning Applications',
    experience: 'Q1 2026',
    students: 'Launching',
    rating: 'Expert',
    avatar: '\ud83d\udc69\u200d\ud83d\udcbb',
    specialties: ['Deep Learning', 'Industrial AI', 'Strategic Implementation'],
  },
  {
    name: 'Future Engineering Leader',
    title: 'Distinguished Sustainability Expert',
    expertise: 'Environmental Engineering Excellence',
    experience: 'Q1 2026',
    students: 'Coming',
    rating: 'Leader',
    avatar: '\ud83c\udf31',
    specialties: ['Circular Economy', 'Green Technologies', 'Executive Strategy'],
  },
];

const TABS = [
  { id: 'overview',     label: 'Career Paths Overview', icon: React.createElement(Map, { size: 18 }) },
  { id: 'intelligence', label: 'Market Intelligence',   icon: React.createElement(Activity, { size: 18 }) },
  { id: 'roadmap',      label: 'Career Roadmap',        icon: React.createElement(Route, { size: 18 }) },
  { id: 'mentorship',   label: 'Mentorship Program',    icon: React.createElement(Users, { size: 18 }) },
  { id: 'assessment',   label: 'Career Assessment',     icon: React.createElement(Target, { size: 18 }) },
];

/* ─── Skeleton helpers ──────────────────────────────────────────────── */
const PathSkeleton = () => (
  React.createElement(Box, { sx: { ...cardSx, p: 3 } },
    React.createElement(Stack, { direction: 'row', spacing: 2, alignItems: 'center', mb: 2 },
      React.createElement(Skeleton, { variant: 'rounded', width: 48, height: 48, sx: { bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 } }),
      React.createElement(Box, { flex: 1 },
        React.createElement(Skeleton, { variant: 'text', width: '70%', sx: { bgcolor: 'rgba(255,255,255,0.08)' } }),
        React.createElement(Skeleton, { variant: 'text', width: '50%', sx: { bgcolor: 'rgba(255,255,255,0.08)' } }),
      )
    ),
    React.createElement(Stack, { direction: 'row', spacing: 1, mb: 2 },
      ...[1,2,3].map(i => React.createElement(Skeleton, { key: i, variant: 'rounded', height: 32, sx: { flex: 1, bgcolor: 'rgba(255,255,255,0.08)' } }))
    ),
    React.createElement(Skeleton, { variant: 'rounded', height: 40, sx: { bgcolor: 'rgba(255,255,255,0.08)' } }),
  )
);

const JobSkeleton = () => (
  React.createElement(Box, { sx: { ...cardSx, p: 2.5 } },
    React.createElement(Skeleton, { variant: 'text', width: '60%', sx: { bgcolor: 'rgba(255,255,255,0.08)', mb: 0.5 } }),
    React.createElement(Skeleton, { variant: 'text', width: '40%', sx: { bgcolor: 'rgba(255,255,255,0.08)', mb: 1.5 } }),
    React.createElement(Stack, { direction: 'row', spacing: 1, mb: 1.5 },
      React.createElement(Skeleton, { variant: 'rounded', width: 90, height: 22, sx: { bgcolor: 'rgba(255,255,255,0.08)' } }),
      React.createElement(Skeleton, { variant: 'rounded', width: 70, height: 22, sx: { bgcolor: 'rgba(255,255,255,0.08)' } }),
    ),
    React.createElement(Skeleton, { variant: 'rounded', height: 34, sx: { bgcolor: 'rgba(255,255,255,0.08)' } }),
  )
);

const SkillChipSkeleton = () =>
  React.createElement(Stack, { direction: 'row', spacing: 1, flexWrap: 'wrap', useFlexGap: true },
    ...Array.from({ length: 8 }).map((_, i) =>
      React.createElement(Skeleton, { key: i, variant: 'rounded', width: 100 + i * 12, height: 28,
        sx: { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '14px' } })
    )
  );

/* ─── Demand bar ────────────────────────────────────────────────────── */
const DemandBar = ({ level, color }) => {
  const pct = level === 'Very High' ? 95 : level === 'High' ? 75 : level === 'Growing' ? 60 : 45;
  return (
    React.createElement(Box, null,
      React.createElement(Typography, { sx: { fontSize: '0.7rem', color: MUT_COLOR, mb: 0.5 } }, 'Demand'),
      React.createElement(Stack, { direction: 'row', alignItems: 'center', spacing: 1 },
        React.createElement(Box, { sx: { flex: 1, height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' } },
          React.createElement(Box, { sx: { width: pct + '%', height: '100%', background: color, borderRadius: 3 } })
        ),
        React.createElement(Typography, { sx: { fontSize: '0.7rem', color, fontWeight: 700, minWidth: 52 } }, level)
      )
    )
  );
};

/* ─── Path card ─────────────────────────────────────────────────────── */
const PathCard = ({ pathKey, path, selected, onClick }) => (
  React.createElement(Box, {
    onClick,
    sx: {
      ...cardSx,
      p: 3,
      cursor: 'pointer',
      borderColor: selected ? (path.accent + '55') : 'rgba(255,255,255,0.08)',
      boxShadow: selected ? ('0 0 0 1px ' + path.accent + '55') : 'none',
    }
  },
    /* header */
    React.createElement(Stack, { direction: 'row', spacing: 2, alignItems: 'flex-start', mb: 2 },
      React.createElement(Box, { sx: { p: 1.2, borderRadius: '10px', background: path.accent + '22', color: path.accent, display: 'flex', flexShrink: 0 } },
        path.icon
      ),
      React.createElement(Box, { flex: 1 },
        React.createElement(Typography, { sx: { color: H_COLOR, fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3 } }, path.title),
        React.createElement(Typography, { sx: { color: SEC_COLOR, fontSize: '0.8rem', mt: 0.4, lineHeight: 1.5 } }, path.description),
      )
    ),
    /* salary + growth chips */
    React.createElement(Stack, { direction: 'row', spacing: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap', useFlexGap: true },
      React.createElement(Chip, {
        icon: React.createElement(DollarSign, { size: 13 }),
        label: path.avgSalary,
        size: 'small',
        sx: { background: AMBER_DIM, color: AMBER, fontWeight: 600, fontSize: '0.75rem', height: 26, '& .MuiChip-icon': { color: AMBER } }
      }),
      React.createElement(Chip, {
        icon: React.createElement(TrendingUp, { size: 12 }),
        label: path.growthRate,
        size: 'small',
        sx: { background: 'rgba(16,185,129,0.12)', color: '#10b981', fontWeight: 600, fontSize: '0.75rem', height: 26, '& .MuiChip-icon': { color: '#10b981' } }
      })
    ),
    /* demand bar */
    React.createElement(Box, { mb: 2 },
      React.createElement(DemandBar, { level: path.demandLevel, color: path.demandColor })
    ),
    /* skill chips */
    React.createElement(Stack, { direction: 'row', spacing: 0.75, flexWrap: 'wrap', useFlexGap: true, mb: 2.5 },
      ...path.skills.map((s, i) => React.createElement(Chip, { key: i, label: s, size: 'small', sx: skillChipSx }))
    ),
    /* industries */
    React.createElement(Typography, { sx: { color: MUT_COLOR, fontSize: '0.75rem', mb: 2.5 } },
      path.industries.join(' \u00b7 ')
    ),
    /* CTA button */
    React.createElement(Button, {
      fullWidth: true,
      size: 'small',
      endIcon: React.createElement(ArrowRight, { size: 15 }),
      sx: {
        background: selected ? path.accent : 'rgba(255,255,255,0.05)',
        color: selected ? '#09090b' : path.accent,
        border: '1px solid ' + path.accent + '55',
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: '0.8rem',
        textTransform: 'none',
        '&:hover': { background: path.accent, color: '#09090b' },
      }
    }, 'Explore Path')
  )
);

/* ─── Job card ──────────────────────────────────────────────────────── */
const JobCard = ({ job }) => {
  const title    = job.title    || job.role     || 'Role';
  const company  = job.company  || job.employer || '';
  const location = job.location || '';
  const salary   = job.salary   || job.salaryRange || '';
  const url      = job.url      || job.link     || '#';

  return (
    React.createElement(Box, { sx: { ...cardSx, p: 2.5 } },
      React.createElement(Typography, { sx: { color: H_COLOR, fontWeight: 600, fontSize: '0.9rem', mb: 0.4 } }, title),
      company && React.createElement(Stack, { direction: 'row', spacing: 0.75, alignItems: 'center', mb: 1 },
        React.createElement(Building, { size: 13, color: MUT_COLOR }),
        React.createElement(Typography, { sx: { color: SEC_COLOR, fontSize: '0.8rem' } }, company)
      ),
      React.createElement(Stack, { direction: 'row', spacing: 1, flexWrap: 'wrap', useFlexGap: true, mb: 1.5 },
        location && React.createElement(Chip, {
          icon: React.createElement(MapPin, { size: 11 }),
          label: location, size: 'small',
          sx: { ...skillChipSx, '& .MuiChip-icon': { color: MUT_COLOR } }
        }),
        salary && React.createElement(Chip, {
          icon: React.createElement(DollarSign, { size: 11 }),
          label: salary, size: 'small',
          sx: { background: AMBER_DIM, color: AMBER, height: 24, fontSize: '0.72rem', '& .MuiChip-icon': { color: AMBER } }
        })
      ),
      React.createElement(Button, {
        component: 'a', href: url, target: '_blank', rel: 'noopener noreferrer',
        size: 'small', fullWidth: true,
        endIcon: React.createElement(ExternalLink, { size: 13 }),
        sx: {
          background: 'rgba(255,255,255,0.05)', color: SEC_COLOR, borderRadius: '8px',
          textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, border: CARD_BDR,
          '&:hover': { background: 'rgba(255,255,255,0.1)', color: H_COLOR },
        }
      }, 'View Job')
    )
  );
};

/* ─── Main component ────────────────────────────────────────────────── */
const CareerPaths = () => {
  const [activeTab,        setActiveTab]        = useState('overview');
  const [selectedPath,     setSelectedPath]     = useState('process-engineer');
  const [currentLevel,     setCurrentLevel]     = useState('junior');
  const [isMobile,         setIsMobile]         = useState(false);
  const [hoveredCard,      setHoveredCard]      = useState(null);
  const [marketStats,      setMarketStats]      = useState(null);
  const [loadingStats,     setLoadingStats]     = useState(false);
  const [selectedSkill,    setSelectedSkill]    = useState(null);

  /* Live API state */
  const [jobs,           setJobs]           = useState([]);
  const [jobsLoading,    setJobsLoading]    = useState(false);
  const [emergingSkills, setEmergingSkills] = useState([]);
  const [skillsLoading,  setSkillsLoading]  = useState(false);

  /* Responsive */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* Market stats (intelligence tab) */
  useEffect(() => {
    if (activeTab === 'intelligence') loadMarketStats();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMarketStats = async () => {
    setLoadingStats(true);
    try {
      const data = await careerService.getMarketStats();
      setMarketStats(data);
    } catch (err) {
      console.error('Market stats fetch failed:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  /* Live API data on mount */
  useEffect(() => {
    const fetchApiData = async () => {
      /* Jobs */
      setJobsLoading(true);
      try {
        const jobsRes  = await CareerApi.searchJobs({ limit: 6 });
        const jobsList = Array.isArray(jobsRes) ? jobsRes : (jobsRes?.jobs || jobsRes?.data || []);
        setJobs(jobsList.slice(0, 6));
      } catch (err) {
        console.error('Jobs fetch failed:', err);
        setJobs([]);
      } finally {
        setJobsLoading(false);
      }

      /* Emerging skills */
      setSkillsLoading(true);
      try {
        const skillsRes  = await CareerApi.getEmergingSkills('engineering', '12months');
        const skillsList = Array.isArray(skillsRes) ? skillsRes : (skillsRes?.skills || skillsRes?.data || []);
        setEmergingSkills(skillsList.slice(0, 8));
      } catch (err) {
        console.error('Skills fetch failed:', err);
        setEmergingSkills([]);
      } finally {
        setSkillsLoading(false);
      }
    };
    fetchApiData();
  }, []);

  const formatNumber = (num) => {
    if (!num) return '0';
    return num >= 1000 ? (num / 1000).toFixed(1) + 'K' : num.toLocaleString();
  };

  const activePath = CAREER_PATHS[selectedPath];

  /* ── Render ─────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, color: H_COLOR, fontFamily: '"Inter","Helvetica","Arial",sans-serif' }}>
      <Header />

      {/* ── Hero ── */}
      <Box component="section" sx={{
        background: 'linear-gradient(135deg, rgba(30,27,75,0.95) 0%, rgba(49,46,129,0.9) 50%, rgba(30,64,175,0.85) 100%)',
        py: isMobile ? 8 : 12, px: 2.5, textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box maxWidth={900} mx="auto">
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            bgcolor: 'rgba(124,58,237,0.2)', color: '#a78bfa',
            px: 2, py: 0.75, borderRadius: '24px', fontSize: '0.85rem',
            fontWeight: 500, mb: 3, border: '1px solid rgba(124,58,237,0.4)',
          }}>
            <Rocket size={15} />
            Career Guidance \u2014 Launching Q1 2026
          </Box>

          <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ fontWeight: 800, lineHeight: 1.1, mb: 3, color: H_COLOR }}>
            Future Engineering{' '}
            <Box component="span" sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Career Paths
            </Box>
          </Typography>

          <Typography sx={{ color: SEC_COLOR, fontSize: isMobile ? '1rem' : '1.2rem', maxWidth: 720, mx: 'auto', mb: 5, lineHeight: 1.7 }}>
            Comprehensive career guidance for ambitious engineers. Strategic roadmaps, skill assessments
            and expert mentorship to accelerate engineering careers worldwide.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Button size="large" startIcon={<Compass size={18} />}
              sx={{ bgcolor: AMBER, color: '#09090b', fontWeight: 700, borderRadius: '12px', textTransform: 'none', px: 4, '&:hover': { bgcolor: '#d97706' } }}>
              Join Launch Waitlist
            </Button>
            <Button size="large" startIcon={<Play size={18} />} variant="outlined"
              sx={{ borderColor: 'rgba(255,255,255,0.3)', color: H_COLOR, borderRadius: '12px', textTransform: 'none', px: 4,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.5)' } }}>
              Preview Paths
            </Button>
          </Stack>

          <Grid container spacing={3} mt={5} justifyContent="center">
            {[
              { value: 'Q1 2026', label: 'Launch Timeline' },
              { value: '5+',      label: 'Career Tracks' },
              { value: 'Expert',  label: 'Industry Guidance' },
              { value: 'Free',    label: 'Early Access' },
            ].map((s, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Typography sx={{ color: AMBER, fontWeight: 800, fontSize: isMobile ? '1.5rem' : '2rem' }}>{s.value}</Typography>
                <Typography sx={{ color: MUT_COLOR, fontSize: '0.8rem', mt: 0.5 }}>{s.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── Stats row ── */}
      <Box component="section" sx={{ bgcolor: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: 4, px: 2.5 }}>
        <Grid container spacing={3} maxWidth={1200} mx="auto" justifyContent="center">
          {[
            { value: 'Q1 2026', label: 'Launch Timeline', icon: React.createElement(TrendingUp, { size: 22 }) },
            { value: 'Future',  label: 'Career Growth',   icon: React.createElement(DollarSign, { size: 22 }) },
            { value: 'Expert',  label: 'Guidance',        icon: React.createElement(Building, { size: 22 }) },
            { value: 'Free',    label: 'Early Access',    icon: React.createElement(Star, { size: 22 }) },
          ].map((s, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={{ ...cardSx, p: 2.5, textAlign: 'center' }}>
                <Box sx={{ color: AMBER, display: 'flex', justifyContent: 'center', mb: 1.5 }}>{s.icon}</Box>
                <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.3rem' }}>{s.value}</Typography>
                <Typography sx={{ color: MUT_COLOR, fontSize: '0.8rem', mt: 0.5 }}>{s.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Tab nav ── */}
      <Box component="nav" sx={{ bgcolor: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: 2, px: 2.5 }}>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap maxWidth={1200} mx="auto">
          {TABS.map(tab => (
            <Button key={tab.id} startIcon={tab.icon} size="small"
              onClick={() => setActiveTab(tab.id)}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '8px',
                px: isMobile ? 1.5 : 2.5, fontSize: isMobile ? '0.78rem' : '0.875rem',
                bgcolor: activeTab === tab.id ? AMBER : 'transparent',
                color: activeTab === tab.id ? '#09090b' : SEC_COLOR,
                '&:hover': {
                  bgcolor: activeTab === tab.id ? AMBER : 'rgba(255,255,255,0.06)',
                  color: activeTab === tab.id ? '#09090b' : H_COLOR,
                },
              }}>
              {!isMobile && tab.label}
            </Button>
          ))}
        </Stack>
      </Box>

      {/* ── Main content ── */}
      <Box component="section" sx={{ maxWidth: 1200, mx: 'auto', px: 2.5, py: isMobile ? 5 : 8 }}>

        {/* ════ OVERVIEW TAB ════ */}
        {activeTab === 'overview' && (
          <>
            <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ color: H_COLOR, fontWeight: 800, textAlign: 'center', mb: 1.5 }}>
              Engineering Career Pathways
            </Typography>
            <Typography sx={{ color: SEC_COLOR, textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 6, lineHeight: 1.7 }}>
              Comprehensive career guidance launching Q1 2026 \u2014 clear progression routes, realistic growth
              expectations and expert mentorship for ambitious engineers worldwide.
            </Typography>

            {/* Career path cards */}
            <Grid container spacing={3}>
              {Object.entries(CAREER_PATHS).map(([key, p]) => (
                <Grid item xs={12} sm={6} lg={4} key={key}>
                  <PathCard pathKey={key} path={p} selected={selectedPath === key} onClick={() => setSelectedPath(key)} />
                </Grid>
              ))}
            </Grid>

            {/* ── Emerging Skills Section ── */}
            <Box mt={8}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                <Zap size={20} color={AMBER} />
                <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.2rem' }}>
                  Emerging Skills in Engineering
                </Typography>
                {skillsLoading && (
                  <Chip label="Fetching live data\u2026" size="small"
                    sx={{ bgcolor: AMBER_DIM, color: AMBER, fontSize: '0.72rem' }} />
                )}
              </Stack>
              <Typography sx={{ color: SEC_COLOR, fontSize: '0.85rem', mb: 2.5 }}>
                Skills trending over the last 12 months across engineering disciplines.
              </Typography>

              {skillsLoading ? (
                <SkillChipSkeleton />
              ) : emergingSkills.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {emergingSkills.map((skill, i) => {
                    const name  = typeof skill === 'string' ? skill : (skill.skill || skill.name || String(skill));
                    const trend = typeof skill === 'object' && skill.trend ? skill.trend : null;
                    return (
                      <Chip
                        key={i}
                        icon={<TrendingUp size={13} />}
                        label={trend ? name + ' +' + trend + '%' : name}
                        size="small"
                        onClick={() => setSelectedSkill(name)}
                        sx={{
                          background: AMBER_DIM, color: AMBER, fontWeight: 600, fontSize: '0.78rem',
                          cursor: 'pointer', height: 28, borderRadius: '14px',
                          '& .MuiChip-icon': { color: AMBER },
                          '&:hover': { background: 'rgba(245,158,11,0.28)' },
                        }}
                      />
                    );
                  })}
                </Stack>
              ) : (
                <Typography sx={{ color: MUT_COLOR, fontSize: '0.85rem' }}>
                  Trending skills data will appear once the API is live.
                </Typography>
              )}
            </Box>

            {/* ── Live Opportunities Section ── */}
            {(jobsLoading || jobs.length > 0) && (
              <Box mt={8}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                  <Briefcase size={20} color={AMBER} />
                  <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.2rem' }}>Live Opportunities</Typography>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981',
                    boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
                    animation: 'pulse 2s infinite',
                  }} />
                </Stack>
                <Typography sx={{ color: SEC_COLOR, fontSize: '0.85rem', mb: 3 }}>
                  Real-time engineering roles sourced from the market \u2014 updated continuously.
                </Typography>

                <Grid container spacing={2}>
                  {jobsLoading
                    ? Array.from({ length: 6 }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}><JobSkeleton /></Grid>
                      ))
                    : jobs.map((job, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}><JobCard job={job} /></Grid>
                      ))
                  }
                </Grid>
              </Box>
            )}
          </>
        )}

        {/* ════ ROADMAP TAB ════ */}
        {activeTab === 'roadmap' && (
          <>
            <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ color: H_COLOR, fontWeight: 800, textAlign: 'center', mb: 1.5 }}>
              {activePath.title} Career Roadmap
            </Typography>
            <Typography sx={{ color: SEC_COLOR, textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 5 }}>
              Step-by-step progression with skills, responsibilities and salary expectations at each level.
            </Typography>

            <Stack direction="row" spacing={1.5} justifyContent="center" mb={4} flexWrap="wrap" useFlexGap>
              {['junior', 'mid', 'senior'].map(lvl => (
                <Button key={lvl} size="small" onClick={() => setCurrentLevel(lvl)}
                  sx={{
                    textTransform: 'none', fontWeight: 600, borderRadius: '20px', px: 2.5,
                    bgcolor: currentLevel === lvl ? AMBER : 'rgba(255,255,255,0.06)',
                    color: currentLevel === lvl ? '#09090b' : SEC_COLOR,
                    border: '1px solid ' + (currentLevel === lvl ? AMBER : 'rgba(255,255,255,0.1)'),
                    '&:hover': { bgcolor: AMBER, color: '#09090b' },
                  }}>
                  {lvl === 'junior' ? 'Junior Level' : lvl === 'mid' ? 'Mid Level' : 'Senior Level'}
                </Button>
              ))}
            </Stack>

            {(() => {
              const lvl = activePath.roadmap[currentLevel];
              return (
                <Box sx={{ ...cardSx, p: { xs: 3, md: 4 } }}>
                  <Box textAlign="center" mb={4}>
                    <Typography sx={{ color: H_COLOR, fontWeight: 800, fontSize: isMobile ? '1.4rem' : '1.75rem', mb: 1 }}>
                      {lvl.title}
                    </Typography>
                    <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" useFlexGap>
                      <Box textAlign="center">
                        <Typography sx={{ color: MUT_COLOR, fontSize: '0.8rem' }}>Experience</Typography>
                        <Typography sx={{ color: H_COLOR, fontWeight: 600 }}>{lvl.duration}</Typography>
                      </Box>
                      <Box textAlign="center">
                        <Typography sx={{ color: MUT_COLOR, fontSize: '0.8rem' }}>Salary Range</Typography>
                        <Typography sx={{ color: AMBER, fontWeight: 700 }}>{lvl.salary}</Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Grid container spacing={4}>
                    {[
                      { icon: React.createElement(Award, { size: 18, color: AMBER }),      title: 'Required Skills',       items: lvl.skills,           dotColor: '#10b981', DotIcon: CheckCircle },
                      { icon: React.createElement(Briefcase, { size: 18, color: AMBER }),   title: 'Key Responsibilities',  items: lvl.responsibilities, dotColor: '#06b6d4', DotIcon: Target },
                      { icon: React.createElement(BookOpen, { size: 18, color: AMBER }),    title: 'Recommended Courses',   items: lvl.courses,          dotColor: '#8b5cf6', DotIcon: GraduationCap },
                      { icon: React.createElement(Rocket, { size: 18, color: AMBER }),      title: 'Next Steps',            items: lvl.nextSteps,        dotColor: AMBER,     DotIcon: ChevronRight },
                    ].map(({ icon, title, items, dotColor, DotIcon }) => (
                      <Grid item xs={12} sm={6} key={title}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                          {icon}
                          <Typography sx={{ color: H_COLOR, fontWeight: 700 }}>{title}</Typography>
                        </Stack>
                        <Stack spacing={0.75}>
                          {items.map((item, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                              <Box sx={{ color: dotColor, flexShrink: 0, mt: 0.2 }}>
                                <DotIcon size={15} />
                              </Box>
                              <Typography sx={{ color: SEC_COLOR, fontSize: '0.875rem', lineHeight: 1.5 }}>{item}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })()}
          </>
        )}

        {/* ════ MENTORSHIP TAB ════ */}
        {activeTab === 'mentorship' && (
          <>
            <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ color: H_COLOR, fontWeight: 800, textAlign: 'center', mb: 1.5 }}>
              Expert Mentorship Program
            </Typography>
            <Typography sx={{ color: SEC_COLOR, textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 6, lineHeight: 1.7 }}>
              Connecting you with industry experts for Q1 2026 launch. Personalised guidance from seasoned
              engineering professionals worldwide.
            </Typography>

            <Grid container spacing={3}>
              {MENTORS.map((mentor, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '3.5rem', mb: 2 }}>{mentor.avatar}</Typography>
                    <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>{mentor.name}</Typography>
                    <Typography sx={{ color: AMBER, fontWeight: 600, fontSize: '0.85rem', mb: 0.75 }}>{mentor.title}</Typography>
                    <Typography sx={{ color: SEC_COLOR, fontSize: '0.8rem', mb: 2 }}>{mentor.expertise}</Typography>

                    <Grid container spacing={1.5} mb={2}>
                      {[
                        { v: mentor.experience, l: 'Experience' },
                        { v: mentor.students,   l: 'Students' },
                        { v: mentor.rating,     l: 'Rating' },
                      ].map(({ v, l }) => (
                        <Grid item xs={4} key={l}>
                          <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '0.85rem' }}>{v}</Typography>
                          <Typography sx={{ color: MUT_COLOR, fontSize: '0.72rem' }}>{l}</Typography>
                        </Grid>
                      ))}
                    </Grid>

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="center" mb={2.5}>
                      {mentor.specialties.map((s, si) => (
                        <Chip key={si} label={s} size="small"
                          sx={{ ...skillChipSx, background: AMBER_DIM, color: AMBER }} />
                      ))}
                    </Stack>

                    <Button fullWidth size="small" startIcon={<MessageCircle size={15} />}
                      sx={{ bgcolor: AMBER, color: '#09090b', fontWeight: 700, borderRadius: '8px',
                        textTransform: 'none', '&:hover': { bgcolor: '#d97706' } }}>
                      Connect with Mentor
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ ...cardSx, p: 4, mt: 6 }}>
              <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.25rem', textAlign: 'center', mb: 3 }}>
                Mentorship Benefits (Q1 2026)
              </Typography>
              <Grid container spacing={3}>
                {[
                  { icon: React.createElement(User, { size: 22 }),    color: AMBER,     title: '1-on-1 Sessions',    desc: 'Personalised career guidance from industry experts launching Q1 2026.' },
                  { icon: React.createElement(Network, { size: 22 }), color: '#06b6d4', title: 'Expert Network',     desc: 'Building connections with professional networks for platform launch.' },
                  { icon: React.createElement(Trophy, { size: 22 }),  color: '#10b981', title: 'Career Acceleration', desc: 'Advanced strategies and insights with the Q1 2026 launch.' },
                ].map(({ icon, color, title, desc }) => (
                  <Grid item xs={12} sm={4} key={title}>
                    <Box textAlign="center" p={2}>
                      <Box sx={{ width: 48, height: 48, bgcolor: color + '20', color, borderRadius: '50%',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                        {icon}
                      </Box>
                      <Typography sx={{ color: H_COLOR, fontWeight: 700, mb: 0.75 }}>{title}</Typography>
                      <Typography sx={{ color: SEC_COLOR, fontSize: '0.85rem', lineHeight: 1.6 }}>{desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        )}

        {/* ════ MARKET INTELLIGENCE TAB ════ */}
        {activeTab === 'intelligence' && (
          <>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" mb={1}>
              <BarChart3 size={26} color={AMBER} />
              <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ color: H_COLOR, fontWeight: 800 }}>
                Career Market Intelligence
              </Typography>
            </Stack>
            <Typography sx={{ color: SEC_COLOR, textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 5 }}>
              Real-time job market data, skill demand insights and salary forecasts to guide your career decisions.
            </Typography>

            {loadingStats ? (
              <Box textAlign="center" py={6}>
                <Box sx={{
                  width: 48, height: 48,
                  border: '4px solid rgba(255,255,255,0.1)',
                  borderTop: '4px solid ' + AMBER,
                  borderRadius: '50%',
                  animation: 'spin 0.9s linear infinite',
                  mx: 'auto', mb: 2,
                }} />
                <Typography sx={{ color: SEC_COLOR }}>Loading market insights\u2026</Typography>
              </Box>
            ) : marketStats && (
              <Box sx={{ ...cardSx, p: 3, mb: 4 }}>
                <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.1rem', mb: 2.5 }}>Market Overview</Typography>
                <Grid container spacing={2}>
                  {[
                    { val: formatNumber(marketStats.totalJobs),   label: 'Total Jobs',   sub: 'Live Data',       grad: 'linear-gradient(135deg,#10b981,#059669)' },
                    { val: formatNumber(marketStats.remoteJobs),  label: 'Remote Jobs',  sub: marketStats.totalJobs > 0 ? ((marketStats.remoteJobs / marketStats.totalJobs * 100).toFixed(1) + '%') : '\u2014', grad: 'linear-gradient(135deg,#3b82f6,#2563eb)' },
                    { val: formatNumber(marketStats.africanJobs), label: 'African Jobs', sub: marketStats.totalJobs > 0 ? ((marketStats.africanJobs / marketStats.totalJobs * 100).toFixed(1) + '%') : '\u2014', grad: 'linear-gradient(135deg,#f59e0b,#d97706)' },
                    { val: formatNumber(marketStats.greenJobs),   label: 'Green Jobs',   sub: 'Sustainability',  grad: 'linear-gradient(135deg,#8b5cf6,#7c3aed)' },
                  ].map(({ val, label, sub, grad }) => (
                    <Grid item xs={6} sm={3} key={label}>
                      <Box sx={{ background: grad, p: 2.5, borderRadius: '12px', textAlign: 'center' }}>
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.5rem' }}>{val}</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', mt: 0.5 }}>{label}</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', mt: 0.25 }}>{sub}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {selectedSkill && (
              <Box mb={4}>
                <SkillDemandChart skill={selectedSkill} onClose={() => setSelectedSkill(null)} />
              </Box>
            )}

            <Box mb={4}><EmergingSkills industry="Technology" onSkillClick={setSelectedSkill} /></Box>
            <Box mb={4}><RecentJobs limit={10} region="Africa" /></Box>

            <Box sx={{ ...cardSx, p: 4 }}>
              <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.1rem', textAlign: 'center', mb: 3 }}>
                Explore Career Tools
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box onClick={() => setSelectedSkill('Python')}
                    sx={{ background: 'linear-gradient(135deg,#1e3a8a,#1e40af)', p: 4, borderRadius: '12px',
                      cursor: 'pointer', textAlign: 'center', transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-2px)' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}><BarChart3 size={40} color="#3b82f6" /></Box>
                    <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>Skill Demand Analysis</Typography>
                    <Typography sx={{ color: SEC_COLOR, fontSize: '0.85rem' }}>Analyse market demand for any skill</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ background: 'linear-gradient(135deg,#15803d,#166534)', p: 4, borderRadius: '12px',
                    cursor: 'pointer', textAlign: 'center', transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-2px)' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}><TrendingUp size={40} color="#10b981" /></Box>
                    <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>Emerging Skills</Typography>
                    <Typography sx={{ color: SEC_COLOR, fontSize: '0.85rem' }}>Discover trending skills in your industry</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {/* ════ ASSESSMENT TAB ════ */}
        {activeTab === 'assessment' && (
          <>
            <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ color: H_COLOR, fontWeight: 800, textAlign: 'center', mb: 1.5 }}>
              Career Assessment Tool
            </Typography>
            <Typography sx={{ color: SEC_COLOR, textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 6, lineHeight: 1.7 }}>
              Comprehensive career assessment launching Q1 2026. Advanced tool will analyse engineering skills,
              interests and career goals to recommend personalised pathways.
            </Typography>

            <Box sx={{ ...cardSx, p: 4, maxWidth: 800, mx: 'auto' }}>
              <Box textAlign="center" mb={4}>
                <Box sx={{ width: 72, height: 72, bgcolor: AMBER_DIM, color: AMBER, borderRadius: '50%',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Target size={36} />
                </Box>
                <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.3rem', mb: 1 }}>
                  Comprehensive Career Assessment
                </Typography>
                <Typography sx={{ color: SEC_COLOR, fontSize: '0.9rem' }}>
                  Take our 15-minute assessment to get personalised career recommendations.
                </Typography>
              </Box>

              <Grid container spacing={2} mb={4}>
                {[
                  { icon: React.createElement(Lightbulb, { size: 22, color: '#8b5cf6' }), title: 'Skills Analysis',   desc: 'Advanced engineering skills assessment coming Q1 2026.' },
                  { icon: React.createElement(Heart,     { size: 22, color: '#ef4444' }), title: 'Career Matching',   desc: 'Intelligent career path recommendations launching Q1 2026.' },
                  { icon: React.createElement(Flag,      { size: 22, color: '#10b981' }), title: 'Goal Setting',      desc: 'Professional roadmapping tools coming with the platform.' },
                ].map(({ icon, title, desc }) => (
                  <Grid item xs={12} sm={4} key={title}>
                    <Box sx={{ ...cardSx, p: 2.5, textAlign: 'center' }}>
                      <Box mb={1}>{icon}</Box>
                      <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>{title}</Typography>
                      <Typography sx={{ color: SEC_COLOR, fontSize: '0.8rem', lineHeight: 1.6 }}>{desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box textAlign="center">
                <Button size="large" startIcon={<Sparkles size={18} />}
                  sx={{ bgcolor: AMBER, color: '#09090b', fontWeight: 700, borderRadius: '12px',
                    textTransform: 'none', px: 4, mb: 1.5, '&:hover': { bgcolor: '#d97706' } }}>
                  Join Assessment Waitlist
                </Button>
                <Typography sx={{ color: MUT_COLOR, fontSize: '0.8rem' }}>
                  Free assessment \u00b7 15 minutes \u00b7 Personalised results
                </Typography>
              </Box>
            </Box>

            <Box sx={{ ...cardSx, p: 4, mt: 5 }}>
              <Typography sx={{ color: H_COLOR, fontWeight: 700, fontSize: '1.1rem', textAlign: 'center', mb: 3 }}>
                Sample Assessment Results
              </Typography>
              <Grid container spacing={2}>
                {[
                  { color: '#10b981', title: 'Top Career Match', value: 'AI Engineer',        sub: '95% compatibility' },
                  { color: '#06b6d4', title: 'Key Strengths',    value: 'Problem solving',    sub: 'Analytics \u00b7 Innovation' },
                  { color: AMBER,     title: 'Growth Areas',     value: 'Leadership',         sub: 'Communication \u00b7 PM' },
                ].map(({ color, title, value, sub }) => (
                  <Grid item xs={12} sm={4} key={title}>
                    <Box sx={{ p: 2.5, bgcolor: color + '15', borderRadius: '12px', border: '1px solid ' + color + '40' }}>
                      <Typography sx={{ color, fontWeight: 700, fontSize: '0.85rem', mb: 0.75 }}>{title}</Typography>
                      <Typography sx={{ color: H_COLOR, fontWeight: 600, fontSize: '0.95rem', mb: 0.4 }}>{value}</Typography>
                      <Typography sx={{ color: SEC_COLOR, fontSize: '0.8rem' }}>{sub}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        )}
      </Box>

      {/* ── CTA footer banner ── */}
      <Box component="section" sx={{
        background: 'linear-gradient(135deg,rgba(30,27,75,0.97) 0%,rgba(49,46,129,0.95) 50%,rgba(30,64,175,0.92) 100%)',
        py: isMobile ? 8 : 10, px: 2.5, textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box maxWidth={800} mx="auto">
          <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ color: H_COLOR, fontWeight: 800, mb: 2 }}>
            \ud83d\ude80 Join the Future of Career Development
          </Typography>
          <Typography sx={{ color: SEC_COLOR, fontSize: isMobile ? '1rem' : '1.1rem', mb: 4, lineHeight: 1.7 }}>
            Get early access to comprehensive career guidance launching Q1 2026. Join ambitious engineers
            building extraordinary careers with expert mentorship and proven pathways.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Button size="large" startIcon={<Rocket size={18} />}
              sx={{ bgcolor: AMBER, color: '#09090b', fontWeight: 700, borderRadius: '12px',
                textTransform: 'none', px: 4, '&:hover': { bgcolor: '#d97706' } }}>
              Join Launch Waitlist
            </Button>
            <Button size="large" startIcon={<MessageCircle size={18} />} variant="outlined"
              sx={{ borderColor: 'rgba(255,255,255,0.3)', color: H_COLOR, borderRadius: '12px',
                textTransform: 'none', px: 4,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.5)' } }}>
              Preview Career Paths
            </Button>
          </Stack>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default CareerPaths;
